import React, { createContext, useContext, ReactNode } from 'react';
import {
  FormProvider as RHFFormProvider,
  UseFormReturn,
  FieldValues,
  Path,
  PathValue
} from 'react-hook-form';

interface FormContextValue<T extends FieldValues = FieldValues> {
  methods: UseFormReturn<T>;
  isSubmitting: boolean;
  isDirty: boolean;
  isValid: boolean;
}

const FormContext = createContext<FormContextValue | null>(null);

interface FormProviderProps<T extends FieldValues = FieldValues> {
  children: ReactNode;
  methods: UseFormReturn<T>;
}

/**
 * Enhanced FormProvider that wraps React Hook Form's FormProvider
 * with additional context values and utilities
 */
export function FormProvider<T extends FieldValues = FieldValues>({
  children,
  methods
}: FormProviderProps<T>) {
  const { formState } = methods;
  const { isSubmitting, isDirty, isValid } = formState;

  const contextValue: FormContextValue<T> = {
    methods,
    isSubmitting,
    isDirty,
    isValid
  };

  return (
    <FormContext.Provider value={contextValue as FormContextValue}>
      <RHFFormProvider {...methods}>{children}</RHFFormProvider>
    </FormContext.Provider>
  );
}

/**
 * Hook to access form context values
 * Provides typed access to form methods and state
 */
export function useFormContext<T extends FieldValues = FieldValues>(): FormContextValue<T> {
  const context = useContext(FormContext);

  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }

  return context as FormContextValue<T>;
}

/**
 * Hook to get a specific field's error message
 * Simplifies error handling in form components
 */
export function useFieldError(fieldName: string): string | undefined {
  const { methods } = useFormContext();
  const { formState } = methods;
  const error = formState.errors[fieldName];

  return error?.message as string | undefined;
}

/**
 * Hook to get a field's value
 * Provides reactive access to field values
 */
export function useFieldValue<T extends FieldValues = FieldValues>(
  fieldName: Path<T>
): PathValue<T, Path<T>> {
  const { methods } = useFormContext<T>();
  return methods.watch(fieldName);
}

/**
 * Hook to set a field's value
 * Provides easy field value updates
 */
export function useSetFieldValue<T extends FieldValues = FieldValues>() {
  const { methods } = useFormContext<T>();

  return (
    fieldName: Path<T>,
    value: PathValue<T, Path<T>>,
    options?: {
      shouldDirty?: boolean;
      shouldTouch?: boolean;
      shouldValidate?: boolean;
    }
  ) => {
    methods.setValue(fieldName, value, options);
  };
}

/**
 * Hook to trigger field validation
 * Useful for custom validation triggers
 */
export function useFieldValidation<T extends FieldValues = FieldValues>() {
  const { methods } = useFormContext<T>();

  return {
    validateField: (fieldName: Path<T>) => methods.trigger(fieldName),
    validateForm: () => methods.trigger(),
    clearErrors: (fieldName?: Path<T>) => methods.clearErrors(fieldName)
  };
}

/**
 * Hook to check if form is in loading/submitting state
 * Useful for disabling form elements during submission
 */
export function useFormStatus() {
  const { isSubmitting, isDirty, isValid } = useFormContext();

  return {
    isSubmitting,
    isDirty,
    isValid,
    canSubmit: isValid && !isSubmitting,
    hasChanges: isDirty
  };
}

export default FormProvider;
